/**
 * Diálogo de acto médico del módulo Colaboradores (2026-08-25).
 * Lo usan la página de Colaboradores y el bloque de acto médico del episodio
 * (ahí con `fijo`: paciente, fecha y episodio clavados).
 *
 * `onSoloValoracion` (5/10/2026, solo desde el episodio): añade al desplegable
 * de tratamientos «Solo valoración (sin tratamiento) — 0,00 €». No es un
 * elemento del tarifario ni crea un acto de colaborador: marca el episodio
 * (no se factura, no se reparte, no se pide el IVA). Ver ActoMedicoEpisodio.
 *
 * Los importes (7/10/2026) se mandan como NÚMERO: una casilla vacía no se
 * manda (el servidor pone lo suyo, p. ej. los fungibles de la configuración)
 * y la coma decimal se entiende como en el tarifario (lib/importeEuros). Antes
 * iban tal cual se tecleaban: el «''» de la casilla de precio que no se usa
 * daba un 422 y ninguna cirugía se podía registrar desde aquí. Lo que no se
 * entiende se dice en el formulario y no se manda.
 *
 * Las CANTIDADES (viales) se leen sin punto de miles (revisión del
 * 7/10/2026, `leerCantidad`): «0,25», «0.25» y «1,5» son decimales; «0.250»
 * es dudosa y se pide que se corrija (antes se leía como 250 viales). El
 * servidor sigue la misma regla.
 *
 * El LUGAR de una cirugía (7/10/2026): «En la clínica» o un hospital del
 * catálogo de precios (`catalogos.lugares`). De él puede depender el reparto
 * del médico (excepciones por médico, en «Porcentajes y fungibles»). Es
 * OBLIGATORIO solo si el médico del acto tiene alguna excepción de cirugía
 * que depende del lugar, desde su fecha (`catalogos.lugar_obligatorio_desde`);
 * a los demás no se les pide. El servidor lo vuelve a comprobar (422).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Banknote, Loader2, Stethoscope, Syringe } from 'lucide-react';
import { api, mensajeError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { dateToStr } from '../lib/citasApi';
import { leerCantidad, leerImporte } from '../lib/importeEuros';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const eur = (v) => (v === null || v === undefined ? '—'
  : Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €');
// Hoy en la hora de la clínica (7/10/2026): con toISOString, entre las 00:00
// y las 02:00 de Madrid salía AYER (es la fecha en UTC).
const hoyISO = () => dateToStr(new Date());
// Valor interno de la opción del desplegable: no choca con ningún nombre del
// catálogo, igual que «__REVISION__» en el de productos.
const SOLO_VALORACION = '__SOLO_VALORACION__';
// «Sin indicar» en el desplegable del lugar (cuando no es obligatorio): no se
// manda.
const SIN_LUGAR = '__SIN_LUGAR__';
const FALTA_LUGAR = 'Indica dónde se ha operado: cambia el reparto de este médico.';

// Los campos numéricos del acto, con su nombre en pantalla.
const CAMPOS_NUMERICOS = [
  ['precio_con_iva', 'Precio (con IVA)'], ['precio_sin_iva', 'Precio sin IVA'],
  ['anestesia', 'Anestesia'], ['instrumentista', 'Instrumentista'],
  ['fungibles', 'Fungibles'], ['coste_producto', 'Coste producto'],
  ['descuento_pct', 'Descuento %'], ['cantidad', 'Cantidad'],
];

/** Deja en `body` los numéricos como número, y fuera los vacíos. Devuelve
 *  el error para el formulario, o '' si todo se entiende. */
function numerosDelActo(body) {
  for (const [campo, etiqueta] of CAMPOS_NUMERICOS) {
    if (!(campo in body)) continue;
    const n = campo === 'cantidad' ? leerCantidad(body[campo]) : leerImporte(body[campo]);
    if (n === null) {
      delete body[campo];
    } else if (Number.isNaN(n)) {
      return `«${etiqueta}»: no se entiende «${String(body[campo]).trim()}». Escribe solo el número, por ejemplo 12,50.`;
    } else {
      body[campo] = n;
    }
  }
  if (body.descuento_pct !== undefined && body.descuento_pct > 100) return '«Descuento %»: tiene que ir de 0 a 100.';
  return '';
}

/** Las líneas de producto con su cantidad como número (viales, admite 0,5;
 *  sin miles: «0.250» es dudosa, no 250). */
function productosDelActo(lineas) {
  const productos = [];
  for (const l of lineas) {
    const cantidad = leerCantidad(l.cantidad);
    if (cantidad === null || Number.isNaN(cantidad) || cantidad <= 0) {
      return { error: `Cantidad de «${l.producto}»: escribe los viales, por ejemplo 1, 0,5 o 0,25 (como mucho dos decimales).` };
    }
    productos.push({ producto: l.producto, cantidad });
  }
  return { productos, error: '' };
}

export default function ColabActividadDialog({
  tipo, onClose, catalogos, medicos, esGestion, medicoActual, onCreada, fijo = null, onSoloValoracion = null,
}) {
  const { user } = useAuth();
  const [f, setF] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  // El formulario se reinicia al abrirse, o si cambia de verdad lo que viene
  // clavado: por VALORES, no por el objeto (revisión del 7/10/2026). Quien
  // abre el diálogo pasa `fijo` como un objeto nuevo en cada render, y cada
  // vez que se repintaba con el diálogo abierto se perdía lo escrito.
  const medicoInicial = medicoActual?.medico_id || '';
  const fijoFecha = fijo?.fecha;
  const fijoPaciente = fijo?.paciente_nombre;
  const fijoPatientId = fijo?.patient_id;
  const fijoEpisodioId = fijo?.episodio_id;
  useEffect(() => {
    if (!tipo) return;
    setError('');
    // `fijo` viene del episodio de la historia: paciente, fecha y episodio
    // quedan clavados (2026-08-25, dirección: que vaya con cada episodio).
    setF({ fecha: fijoFecha || hoyISO(), medico_id: medicoInicial,
           paciente_nombre: fijoPaciente || '', patient_id: fijoPatientId,
           episodio_id: fijoEpisodioId,
           // Varias líneas de producto en un tratamiento (2026-08-27, dirección:
           // «en labio se puede poner un RHA 2 y un 3»).
           lineas: [{ producto: '', cantidad: '' }] });
  }, [tipo, medicoInicial, fijoFecha, fijoPaciente, fijoPatientId, fijoEpisodioId]);
  if (!tipo) return null;
  const set = (k, v) => { setError(''); setF((p) => ({ ...p, [k]: v })); };

  const tratamientoSel = (catalogos?.tratamientos || []).find((t) => t.nombre === f.tratamiento);
  const esSoloValoracion = !!onSoloValoracion && tipo === 'tratamiento' && f.tratamiento === SOLO_VALORACION;
  const lineas = f.lineas || [{ producto: '', cantidad: '' }];
  const esRevision = lineas[0]?.producto === '__REVISION__';
  const setLinea = (i, k, v) => {
    setError('');
    setF((prev) => ({
      ...prev,
      lineas: (prev.lineas || []).map((l, idx) => (idx === i ? { ...l, [k]: v } : l)),
    }));
  };
  const addLinea = () => setF((prev) => ({ ...prev, lineas: [...(prev.lineas || []), { producto: '', cantidad: '' }] }));
  const quitarLinea = (i) => setF((prev) => ({
    ...prev,
    lineas: (prev.lineas || []).filter((_, idx) => idx !== i),
  }));
  const lineasValidas = lineas.filter((l) => l.producto && l.producto !== '__REVISION__');
  const costeLineas = lineasValidas.reduce((acc, l) => {
    const prod = (catalogos?.productos || []).find((pp) => pp.nombre === l.producto);
    const cantidad = leerCantidad(l.cantidad);
    return acc + (prod ? Number(prod.coste_vial || 0) * (Number.isFinite(cantidad) && cantidad > 0 ? cantidad : 1) : 0);
  }, 0);

  // El lugar de la cirugía: obligatorio solo si el reparto de ESTE médico
  // depende de él en esa fecha. El médico del acto: el elegido (gestión) o
  // quien lo registra (un médico se apunta a sí mismo).
  const lugares = Array.isArray(catalogos?.lugares) ? catalogos.lugares : [];
  const medicoDelActo = esGestion ? f.medico_id : (medicoActual?.medico_id || user?.id);
  const desdeLugar = medicoDelActo ? (catalogos?.lugar_obligatorio_desde || {})[medicoDelActo] : null;
  const lugarObligatorio = tipo === 'cirugia' && !!desdeLugar && String(f.fecha || '') >= desdeLugar;
  const lugarElegido = !!f.lugar_id && f.lugar_id !== SIN_LUGAR;

  const guardar = async () => {
    setError('');
    if (esSoloValoracion) {
      setGuardando(true);
      // Lo guarda el editor del episodio (y avisa él): aquí solo se cierra.
      try {
        if (await onSoloValoracion()) onCreada();
      } finally { setGuardando(false); }
      return;
    }
    const body = { tipo, ...f, medico_id: f.medico_id || undefined };
    delete body.lineas;
    // Una revisión sin tratamiento no lleva descuento (ni se ve la casilla).
    if (tipo === 'tratamiento' && esRevision) delete body.descuento_pct;
    // El lugar solo va en las cirugías, y «Sin indicar» no se manda.
    if (tipo !== 'cirugia' || !lugarElegido) delete body.lugar_id;
    if (lugarObligatorio && !lugarElegido) { setError(FALTA_LUGAR); return; }
    let problema = numerosDelActo(body);
    if (!problema && tipo === 'tratamiento') {
      if (esRevision) {
        body.revision_sin_tratamiento = true;
      } else {
        const r = productosDelActo(lineasValidas);
        problema = r.error;
        body.productos = r.productos;
      }
    }
    if (problema) { setError(problema); return; }
    setGuardando(true);
    try {
      const r = await api.post('/api/colaboradores/actividades', body);
      toast.success(`Anotado — su parte: ${eur(r.data?.parte_medico)}`);
      onCreada();
    } catch (e) {
      const msg = mensajeError(e, 'No se pudo guardar');
      setError(msg);
      toast.error(msg);
    } finally { setGuardando(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md max-h-[88dvh] overflow-y-auto" data-testid="colab-dialogo">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tipo === 'tratamiento' ? <Syringe className="w-5 h-5 text-[#2E6E5E]" /> : <Stethoscope className="w-5 h-5 text-[#2E6E5E]" />}
            {tipo === 'tratamiento' ? 'Nuevo tratamiento' : 'Nueva cirugía'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {esGestion && (
            <div>
              <Label>Médico</Label>
              <Select value={f.medico_id || ''} onValueChange={(v) => set('medico_id', v)}>
                <SelectTrigger data-testid="colab-medico-select"><SelectValue placeholder="Elige médico…" /></SelectTrigger>
                <SelectContent>
                  {medicos.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha</Label>
              <Input type="date" value={f.fecha || ''} onChange={(e) => set('fecha', e.target.value)} disabled={!!fijo} data-testid="colab-fecha" />
            </div>
            <div>
              <Label>Paciente</Label>
              <Input value={f.paciente_nombre || ''} onChange={(e) => set('paciente_nombre', e.target.value)}
                placeholder="Nombre del paciente" disabled={!!fijo} data-testid="colab-paciente" />
            </div>
          </div>

          {tipo === 'tratamiento' ? (
            <>
              <div>
                <Label>Tratamiento</Label>
                <Select value={f.tratamiento || ''} onValueChange={(v) => set('tratamiento', v)}>
                  <SelectTrigger data-testid="colab-tratamiento"><SelectValue placeholder="Del catálogo…" /></SelectTrigger>
                  <SelectContent>
                    {onSoloValoracion && (
                      <SelectItem value={SOLO_VALORACION}>Solo valoración (sin tratamiento) — {eur(0)}</SelectItem>
                    )}
                    {(catalogos?.tratamientos || []).map((t) => (
                      <SelectItem key={t.id} value={t.nombre}>{t.nombre} — {eur(t.pvp)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {esSoloValoracion && (
                <p className="text-xs rounded-md bg-[rgba(46,110,94,0.06)] border border-[#2E6E5E]/30 text-[#13231E] px-2.5 py-1.5"
                  data-testid="colab-solo-valoracion-nota">
                  Sin tratamiento: <strong>0,00 €</strong>, no se factura ni entra en el reparto y no hay
                  IVA que marcar. Queda escrito en el evolutivo.
                </p>
              )}
              {!esSoloValoracion && (
                <div>
                  <Label>Productos usados <span className="text-red-600">*</span></Label>
                  {lineas.map((linea, i) => (
                    <div key={i} className={`grid ${esRevision ? 'grid-cols-1' : 'grid-cols-[1fr_92px_32px]'} gap-2 mt-1.5 items-center`}>
                      <Select value={linea.producto || ''} onValueChange={(v) => {
                        if (i === 0 && v === '__REVISION__') {
                          setF((prev) => ({ ...prev, lineas: [{ producto: '__REVISION__', cantidad: '' }], revision_sin_tratamiento: true }));
                        } else {
                          setLinea(i, 'producto', v);
                          if (i === 0) set('revision_sin_tratamiento', false);
                        }
                      }}>
                        <SelectTrigger data-testid={`colab-producto-${i}`}><SelectValue placeholder="Del catálogo… (obligatorio)" /></SelectTrigger>
                        <SelectContent>
                          {i === 0 && (
                            <SelectItem value="__REVISION__">— REVISIÓN SIN TRATAMIENTO (no se aplicó producto) —</SelectItem>
                          )}
                          {(catalogos?.productos || []).map((p) => (
                            <SelectItem key={p.id} value={p.nombre}>{p.nombre} — {eur(p.coste_vial)}/vial</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!esRevision && (
                        <Input inputMode="decimal" value={linea.cantidad ?? ''} onChange={(e) => setLinea(i, 'cantidad', e.target.value)}
                          placeholder="viales" title="Cantidad (viales)" data-testid={`colab-cantidad-${i}`} />
                      )}
                      {!esRevision && (
                        lineas.length > 1 ? (
                          <button type="button" onClick={() => quitarLinea(i)}
                            className="text-muted-foreground hover:text-destructive text-lg leading-none"
                            title="Quitar esta línea" data-testid={`colab-quitar-linea-${i}`}>×</button>
                        ) : <span />
                      )}
                    </div>
                  ))}
                  {!esRevision && (
                    <Button type="button" size="sm" variant="outline" className="mt-2 h-8 text-xs"
                      onClick={addLinea} data-testid="colab-anadir-producto">
                      + Añadir otro producto
                    </Button>
                  )}
                </div>
              )}
              {esRevision && !esSoloValoracion && (
                <p className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 px-2.5 py-1.5">
                  Se registrará la visita como <strong>revisión sin tratamiento</strong>, con importes a cero.
                </p>
              )}
              {!esRevision && !esSoloValoracion && (
                <div>
                  <Label>Descuento % <span className="text-muted-foreground text-xs">(opcional)</span></Label>
                  <Input inputMode="decimal" value={f.descuento_pct ?? ''} onChange={(e) => set('descuento_pct', e.target.value)}
                    placeholder="0" data-testid="colab-descuento" />
                </div>
              )}
              {tratamientoSel && !esRevision && lineasValidas.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  PVP fijo {eur(tratamientoSel.pvp)} · productos {eur(costeLineas)} — el resto lo calcula el sistema.
                </p>
              )}
            </>
          ) : (
            <>
              <div>
                <Label>Intervención <span className="text-red-600">*</span></Label>
                <Select value={f.intervencion || ''} onValueChange={(v) => {
                  const c = (catalogos?.cirugias || []).find((x) => x.nombre === v);
                  setF((prev) => ({
                    ...prev, intervencion: v,
                    // El sugerido solo rellena la casilla: el precio final lo
                    // escribe el cirujano (2026-08-25, dirección).
                    precio_con_iva: c && !c.sin_iva ? c.sugerido : '',
                    precio_sin_iva: c && c.sin_iva ? c.sugerido : '',
                  }));
                }}>
                  <SelectTrigger data-testid="colab-intervencion"><SelectValue placeholder="Del catálogo… (obligatorio)" /></SelectTrigger>
                  <SelectContent>
                    {(catalogos?.cirugias || []).map((c) => (
                      <SelectItem key={c.id} value={c.nombre}>
                        {c.nombre} — sugerido {eur(c.sugerido)}{c.sin_iva ? ' (sin IVA)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground mt-1">El sugerido es orientativo: escribe debajo el precio que consideres.</p>
              </div>
              <div>
                <Label>
                  Lugar{' '}
                  {lugarObligatorio
                    ? <span className="text-red-600">*</span>
                    : <span className="text-muted-foreground text-xs">(opcional)</span>}
                </Label>
                <Select value={lugarElegido ? f.lugar_id : ''} onValueChange={(v) => set('lugar_id', v === SIN_LUGAR ? '' : v)}>
                  <SelectTrigger data-testid="colab-lugar">
                    <SelectValue placeholder={lugarObligatorio ? 'Dónde se ha operado… (obligatorio)' : 'Dónde se ha operado…'} />
                  </SelectTrigger>
                  <SelectContent>
                    {!lugarObligatorio && <SelectItem value={SIN_LUGAR}>Sin indicar</SelectItem>}
                    {lugares.map((l) => <SelectItem key={l.id} value={l.id}>{l.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                {lugarObligatorio && (
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="colab-lugar-aviso">
                    Obligatorio: el reparto de este médico cambia según dónde se opere.
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Precio (con IVA)</Label>
                  <Input inputMode="decimal" value={f.precio_con_iva ?? ''} onChange={(e) => set('precio_con_iva', e.target.value)}
                    placeholder="0,00" data-testid="colab-precio" />
                </div>
                <div>
                  <Label>o precio SIN IVA</Label>
                  <Input inputMode="decimal" value={f.precio_sin_iva ?? ''} onChange={(e) => set('precio_sin_iva', e.target.value)}
                    placeholder="si es exenta" data-testid="colab-precio-siniva" />
                </div>
                <div>
                  <Label>Anestesia</Label>
                  <Input inputMode="decimal" value={f.anestesia ?? ''} onChange={(e) => set('anestesia', e.target.value)} placeholder="0,00"
                    data-testid="colab-anestesia" />
                </div>
                <div>
                  <Label>Instrumentista</Label>
                  <Input inputMode="decimal" value={f.instrumentista ?? ''} onChange={(e) => set('instrumentista', e.target.value)} placeholder="0,00"
                    data-testid="colab-instrumentista" />
                </div>
                <div>
                  <Label>Fungibles</Label>
                  <Input inputMode="decimal" value={f.fungibles ?? ''} onChange={(e) => set('fungibles', e.target.value)}
                    placeholder={`${catalogos?.config?.fungibles_defecto ?? 50} (fijo)`} data-testid="colab-fungibles" />
                </div>
                <div>
                  <Label>Coste producto <span className="text-muted-foreground text-xs">(si lo hay)</span></Label>
                  <Input inputMode="decimal" value={f.coste_producto ?? ''} onChange={(e) => set('coste_producto', e.target.value)} placeholder="0,00"
                    data-testid="colab-coste-producto" />
                </div>
              </div>
            </>
          )}

          {error && (
            <p className="text-sm text-red-700" role="alert" data-testid="colab-error">{error}</p>
          )}
          <Button onClick={guardar}
            // Gestión elige el médico (desde el episodio viene ya elegido si el
            // autor es un médico colaborador): sin él, el servidor da 422.
            disabled={guardando || (esSoloValoracion ? false : (esGestion && !f.medico_id) || !f.paciente_nombre?.trim() || (tipo === 'tratamiento'
              ? (esRevision ? false : !(f.tratamiento && lineasValidas.length > 0
                  && lineasValidas.every((l) => String(l.cantidad ?? '').trim())))
              : !(f.intervencion && (String(f.precio_con_iva ?? '').trim() || String(f.precio_sin_iva ?? '').trim())
                  && (!lugarObligatorio || lugarElegido))))}
            className="w-full bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="colab-guardar">
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Banknote className="w-4 h-4 mr-2" />}
            {esSoloValoracion ? 'Guardar «Solo valoración»' : 'Guardar — el sistema calcula el resto'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
